import classNames from 'classnames';
import {
    ChangeEvent,
    FC,
    KeyboardEvent,
    forwardRef,
    useEffect,
    useRef,
    useState,
} from 'react';

import styles from './VerificationInput.module.css';

type Props = {
    value?: string;
    length?: number;
    error?: boolean;
    disabled?: boolean;
    validChars?: string;
    className?: string;
    placeholder?: string;
    autoFocus?: boolean;
    onChange?: (value: string) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    onComplete: (value: string) => void;
};

export const VerificationInput: FC<Props> = forwardRef(
    (
        {
            value,
            onChange,
            onFocus,
            onBlur,
            onComplete,
            className,
            length = 4,
            validChars = '0-9',
            placeholder = '',
            autoFocus = true,
            disabled,
            error,
        },
        ref,
    ) => {
        const [localValue, setLocalValue] = useState('');
        const [localError, setLocalError] = useState(false);
        const [isActive, setActive] = useState(false);

        const inputRef = useRef<HTMLInputElement | null>(null);

        useEffect(() => {
            if (autoFocus) {
                inputRef.current?.focus();
            }
        }, [autoFocus]);

        useEffect(() => {
            setLocalError(Boolean(error));
        }, [error]);

        useEffect(() => {
            setLocalError(false);
        }, [localValue]);

        const handleClick = () => {
            inputRef.current?.focus();
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(
                    event.key,
                )
            ) {
                event.preventDefault();
            }
        };

        const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
            const newInputVal = event.target.value.replace(/\s/g, '');

            if (RegExp(`^[${validChars}]{0,${length}}$`).test(newInputVal)) {
                if (onChange) {
                    onChange?.(newInputVal);
                }
                setLocalValue(newInputVal);

                if (newInputVal.length === length) {
                    onComplete?.(newInputVal);
                }
            }
        };

        const getValue = () => {
            return value ?? localValue;
        };

        return (
            <div className={classNames(styles.block, className)}>
                <input
                    aria-label="verification input"
                    value={getValue()}
                    onChange={handleInputChange}
                    ref={(node) => {
                        inputRef.current = node;
                        if (typeof ref === 'function') {
                            ref(node);
                        } else if (ref) {
                            ref.current = node;
                        }
                    }}
                    className={styles.input}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        setActive(true);
                        onFocus?.();
                    }}
                    onBlur={() => {
                        setActive(false);
                        onBlur?.();
                    }}
                    type="number"
                    pattern="[0-9]*"
                    inputMode="numeric"
                />
                <div
                    className={styles.container}
                    onClick={() => inputRef.current?.focus()}
                >
                    {[...Array(length)].map((_, i) => (
                        <div
                            className={classNames(styles.character, {
                                [styles.selected]:
                                    (getValue().length === i ||
                                        (getValue().length === i + 1 &&
                                            length === i + 1)) &&
                                    isActive,
                                [styles.inactive]:
                                    getValue().length < i || Boolean(disabled),
                                [styles.error]: Boolean(localError),
                            })}
                            onClick={handleClick}
                            key={i}
                        >
                            {getValue()[i] || placeholder}
                        </div>
                    ))}
                </div>
            </div>
        );
    },
);
